/**
 * NATEX OPPORTUNITY SCORE - CALCULATION ENGINE
 *
 * Professional-grade calculator that transforms raw indicator data
 * into institutional-quality investment scores.
 *
 * Flow:
 * Raw Data → Score Indicator (0-10) → Aggregate Factor (0-100)
 *         → Aggregate Pillar (0-100) → Apply Adjustments → Final Score
 */

import {
  Pillar,
  Factor,
  DataSource,
  ScoreSignal,
  TrendDirection,
  Indicator,
  IndicatorScore,
  FactorScore,
  PillarScore,
  NatexScore,
  KeyDriver,
  ScoreAdjustment,
  CountryIndicatorData,
  CountryScoreInput,
  PILLAR_WEIGHTS,
  PILLAR_CONFIG,
  FACTOR_WEIGHTS,
  PILLAR_FACTORS,
  FACTOR_NAMES,
  SCORE_THRESHOLDS,
  SCORE_GRADES,
} from './types';

import {
  ALL_INDICATORS,
  INDICATOR_BY_ID,
  INDICATORS_BY_PILLAR,
  INDICATORS_BY_FACTOR,
} from './indicators';

// ============================================================================
// INDICATOR SCORING (Raw Value → 0-10 Score)
// ============================================================================

/**
 * Score a single indicator value using its thresholds
 * Returns a score from 0-10
 */
export function scoreIndicator(
  value: number | null,
  indicator: Indicator
): number {
  if (value === null || value === undefined) {
    return 0; // No data = 0 score
  }

  const { thresholds, isHigherBetter } = indicator;

  // Find matching threshold
  for (const threshold of thresholds) {
    if (value >= threshold.minValue && value < threshold.maxValue) {
      return threshold.score;
    }
    // Handle edge case for last threshold (inclusive maxValue)
    if (threshold === thresholds[thresholds.length - 1] && value === threshold.maxValue) {
      return threshold.score;
    }
  }

  // If no threshold matches, return based on direction
  if (isHigherBetter) {
    return value > (thresholds[thresholds.length - 1]?.maxValue ?? 100) ? 10 : 0;
  } else {
    return value < (thresholds[0]?.minValue ?? 0) ? 10 : 0;
  }
}

/**
 * Get status label for a scored indicator
 */
export function getIndicatorStatus(
  score: number
): 'excellent' | 'good' | 'fair' | 'poor' | 'critical' | 'no_data' {
  if (score >= 9) return 'excellent';
  if (score >= 7) return 'good';
  if (score >= 5) return 'fair';
  if (score >= 3) return 'poor';
  if (score > 0) return 'critical';
  return 'no_data';
}

/**
 * Score all indicators for a country and return IndicatorScore objects
 */
export function scoreAllIndicators(
  countryData: CountryIndicatorData
): IndicatorScore[] {
  const scoredIndicators: IndicatorScore[] = [];

  for (const indicator of ALL_INDICATORS) {
    const rawValue = countryData.indicators[indicator.id] ?? null;
    const score = scoreIndicator(rawValue, indicator);

    scoredIndicators.push({
      indicatorId: indicator.id,
      rawValue,
      score,
      weightedScore: score * indicator.weight,
      source: indicator.source,
      asOfDate: countryData.asOfDate,
      isEstimated: false,
    });
  }

  return scoredIndicators;
}

// ============================================================================
// FACTOR CALCULATION (Indicator Scores → Factor Score 0-100)
// ============================================================================

/**
 * Calculate a factor score from its indicators
 * Returns 0-100
 */
export function calculateFactor(
  factor: Factor,
  scoredIndicators: IndicatorScore[]
): FactorScore {
  const factorIndicatorDefs = INDICATORS_BY_FACTOR.get(factor) || [];

  // Filter scored indicators to those belonging to this factor
  const factorIndicators = scoredIndicators.filter(si =>
    factorIndicatorDefs.some(def => def.id === si.indicatorId)
  );

  if (factorIndicators.length === 0) {
    return {
      factor,
      name: FACTOR_NAMES[factor],
      score: 0,
      weight: FACTOR_WEIGHTS[factor],
      weightedScore: 0,
      indicators: [],
      dataCompleteness: 0,
    };
  }

  // Calculate weighted average of indicator scores
  // Indicator scores are 0-10, convert to 0-100
  let totalWeight = 0;
  let weightedSum = 0;
  let dataCount = 0;

  for (const scoredInd of factorIndicators) {
    const indicatorDef = INDICATOR_BY_ID.get(scoredInd.indicatorId);
    if (!indicatorDef) continue;

    const weight = indicatorDef.weight;
    weightedSum += (scoredInd.score / 10) * 100 * weight;
    totalWeight += weight;

    if (scoredInd.rawValue !== null) {
      dataCount++;
    }
  }

  const factorScore = totalWeight > 0 ? weightedSum / totalWeight : 0;
  const factorWeight = FACTOR_WEIGHTS[factor];

  return {
    factor,
    name: FACTOR_NAMES[factor],
    score: Math.round(factorScore * 100) / 100,
    weight: factorWeight,
    weightedScore: Math.round(factorScore * factorWeight * 100) / 100,
    indicators: factorIndicators,
    dataCompleteness: factorIndicatorDefs.length > 0
      ? dataCount / factorIndicatorDefs.length
      : 0,
  };
}

// ============================================================================
// PILLAR CALCULATION (Factor Scores → Pillar Score 0-100)
// ============================================================================

/**
 * Calculate a pillar score from its factors
 * Returns 0-100
 */
export function calculatePillar(
  pillar: Pillar,
  scoredIndicators: IndicatorScore[],
  previousPillarScore?: number
): PillarScore {
  const factorList = PILLAR_FACTORS[pillar];
  const factors: FactorScore[] = factorList.map(factor =>
    calculateFactor(factor, scoredIndicators)
  );

  // Calculate weighted average of factor scores
  let totalWeight = 0;
  let weightedSum = 0;

  for (const factor of factors) {
    if (factor.weight > 0) {
      weightedSum += factor.score * factor.weight;
      totalWeight += factor.weight;
    }
  }

  const pillarScore = totalWeight > 0 ? weightedSum / totalWeight : 0;
  const pillarWeight = PILLAR_WEIGHTS[pillar];
  const config = PILLAR_CONFIG[pillar];

  // Determine trend
  let trend: TrendDirection = TrendDirection.STABLE;
  let changeFromPrevious = 0;

  if (previousPillarScore !== undefined) {
    changeFromPrevious = pillarScore - previousPillarScore;
    if (changeFromPrevious > 2) trend = TrendDirection.RISING;
    else if (changeFromPrevious < -2) trend = TrendDirection.FALLING;
  }

  return {
    pillar,
    name: config.name,
    score: Math.round(pillarScore * 100) / 100,
    weight: pillarWeight,
    contribution: Math.round(pillarScore * pillarWeight * 100) / 100,
    factors,
    trend,
    changeFromPrevious: Math.round(changeFromPrevious * 100) / 100,
    icon: config.icon,
    color: config.color,
  };
}

/**
 * Calculate all pillar scores
 */
export function calculateAllPillars(
  scoredIndicators: IndicatorScore[],
  previousScores?: Record<Pillar, number>
): PillarScore[] {
  return Object.values(Pillar).map(pillar =>
    calculatePillar(pillar, scoredIndicators, previousScores?.[pillar])
  );
}

/**
 * Calculate overall score from pillars
 * Returns 0-100
 */
export function calculateOverallScore(pillars: PillarScore[]): number {
  const score = pillars.reduce((sum, pillar) => sum + pillar.contribution, 0);
  return Math.round(score * 100) / 100;
}

// ============================================================================
// SIGNAL, STARS, GRADE DERIVATION
// ============================================================================

/**
 * Get signal from overall score
 * Uses fixed order to ensure correct threshold matching
 */
export function getSignalFromScore(score: number): ScoreSignal {
  // Direct threshold checks in priority order (highest first)
  if (score >= 85) return ScoreSignal.STRONG_BUY;
  if (score >= 70) return ScoreSignal.BUY;
  if (score >= 55) return ScoreSignal.HOLD;
  if (score >= 40) return ScoreSignal.UNDERWEIGHT;
  return ScoreSignal.AVOID;
}

/**
 * Get stars from overall score
 */
export function getStarsFromScore(score: number): 1 | 2 | 3 | 4 | 5 {
  if (score >= 85) return 5;
  if (score >= 70) return 4;
  if (score >= 55) return 3;
  if (score >= 40) return 2;
  return 1;
}

/**
 * Get grade from overall score
 */
export function getGradeFromScore(score: number): string {
  if (score >= 95) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 85) return 'A-';
  if (score >= 80) return 'B+';
  if (score >= 75) return 'B';
  if (score >= 70) return 'B-';
  if (score >= 65) return 'C+';
  if (score >= 60) return 'C';
  if (score >= 55) return 'C-';
  if (score >= 45) return 'D';
  return 'F';
}

// ============================================================================
// ADJUSTMENTS
// ============================================================================

/**
 * Apply manual adjustments to base score
 */
export function applyAdjustments(
  baseScore: number,
  adjustments: ScoreAdjustment[]
): number {
  let adjustedScore = baseScore;
  const now = new Date();

  for (const adjustment of adjustments) {
    // Check if within date range
    if (adjustment.validFrom > now) continue;
    if (adjustment.validTo && adjustment.validTo < now) continue;

    // Apply adjustment (capped at +/- 10 points per adjustment)
    const cappedImpact = Math.max(-10, Math.min(10, adjustment.impact));
    adjustedScore += cappedImpact;
  }

  // Ensure score stays in 0-100 range
  return Math.max(0, Math.min(100, Math.round(adjustedScore * 100) / 100));
}

// ============================================================================
// KEY DRIVERS IDENTIFICATION
// ============================================================================

/**
 * Identify key drivers (positive and negative) of the score
 */
export function identifyDrivers(
  scoredIndicators: IndicatorScore[],
  pillars: PillarScore[]
): KeyDriver[] {
  const drivers: KeyDriver[] = [];

  // Sort indicators by their score (for finding strengths and weaknesses)
  const sortedByScore = [...scoredIndicators]
    .filter(si => si.rawValue !== null)
    .sort((a, b) => b.score - a.score);

  // Top 3 strengths (score >= 8)
  const strengths = sortedByScore.filter(ind => ind.score >= 8).slice(0, 3);
  for (const strength of strengths) {
    const indicator = INDICATOR_BY_ID.get(strength.indicatorId);
    if (!indicator) continue;

    drivers.push({
      indicator: indicator.name,
      indicatorId: strength.indicatorId,
      description: `${indicator.name}: ${strength.rawValue}${indicator.unit}`,
      impact: Math.round(strength.score * indicator.weight * 10) / 10,
      isPositive: true,
      category: 'strength',
      pillar: indicator.pillar,
      rawValue: strength.rawValue,
      unit: indicator.unit,
    });
  }

  // Top 3 weaknesses (score <= 4, excluding no data)
  const weaknesses = sortedByScore
    .filter(ind => ind.score <= 4 && ind.score > 0)
    .reverse()
    .slice(0, 3);

  for (const weakness of weaknesses) {
    const indicator = INDICATOR_BY_ID.get(weakness.indicatorId);
    if (!indicator) continue;

    drivers.push({
      indicator: indicator.name,
      indicatorId: weakness.indicatorId,
      description: `${indicator.name}: ${weakness.rawValue ?? 'N/A'}${indicator.unit}`,
      impact: -Math.round((10 - weakness.score) * indicator.weight * 10) / 10,
      isPositive: false,
      category: 'weakness',
      pillar: indicator.pillar,
      rawValue: weakness.rawValue,
      unit: indicator.unit,
    });
  }

  // Identify catalysts (from Catalyst Pipeline pillar with high scores)
  const catalystPillar = pillars.find(p => p.pillar === Pillar.CATALYST_PIPELINE);
  if (catalystPillar && catalystPillar.score >= 70) {
    const topCatalystIndicator = scoredIndicators
      .filter(si => {
        const ind = INDICATOR_BY_ID.get(si.indicatorId);
        return ind?.pillar === Pillar.CATALYST_PIPELINE && si.score >= 7;
      })
      .sort((a, b) => b.score - a.score)[0];

    if (topCatalystIndicator) {
      const indicator = INDICATOR_BY_ID.get(topCatalystIndicator.indicatorId);
      if (indicator) {
        drivers.push({
          indicator: indicator.name,
          indicatorId: topCatalystIndicator.indicatorId,
          description: `Catalyst: ${indicator.name}`,
          impact: Math.round(topCatalystIndicator.score * 0.5 * 10) / 10,
          isPositive: true,
          category: 'catalyst',
          pillar: indicator.pillar,
          rawValue: topCatalystIndicator.rawValue,
          unit: indicator.unit,
        });
      }
    }
  }

  // Identify risks (from Risk Profile pillar with low scores)
  const riskPillar = pillars.find(p => p.pillar === Pillar.RISK_PROFILE);
  if (riskPillar && riskPillar.score < 60) {
    const topRiskIndicator = scoredIndicators
      .filter(si => {
        const ind = INDICATOR_BY_ID.get(si.indicatorId);
        return ind?.pillar === Pillar.RISK_PROFILE && si.score <= 5 && si.score > 0;
      })
      .sort((a, b) => a.score - b.score)[0];

    if (topRiskIndicator) {
      const indicator = INDICATOR_BY_ID.get(topRiskIndicator.indicatorId);
      if (indicator) {
        drivers.push({
          indicator: indicator.name,
          indicatorId: topRiskIndicator.indicatorId,
          description: `Risk: ${indicator.name}`,
          impact: -Math.round((10 - topRiskIndicator.score) * 0.5 * 10) / 10,
          isPositive: false,
          category: 'risk',
          pillar: indicator.pillar,
          rawValue: topRiskIndicator.rawValue,
          unit: indicator.unit,
        });
      }
    }
  }

  return drivers;
}

/**
 * Extract strengths as string array
 */
function extractStrengths(drivers: KeyDriver[]): string[] {
  return drivers
    .filter(d => d.isPositive && (d.category === 'strength' || d.category === 'catalyst'))
    .map(d => d.description);
}

/**
 * Extract weaknesses as string array
 */
function extractWeaknesses(drivers: KeyDriver[]): string[] {
  return drivers
    .filter(d => !d.isPositive && (d.category === 'weakness' || d.category === 'risk'))
    .map(d => d.description);
}

// ============================================================================
// DATA QUALITY
// ============================================================================

/**
 * Calculate data completeness
 */
export function calculateDataQuality(
  countryData: CountryIndicatorData
): number {
  let dataCount = 0;

  for (const indicator of ALL_INDICATORS) {
    const value = countryData.indicators[indicator.id];
    if (value !== null && value !== undefined) {
      dataCount++;
    }
  }

  return Math.round((dataCount / ALL_INDICATORS.length) * 100) / 100;
}

// ============================================================================
// PEER RANKING (Simplified)
// ============================================================================

/**
 * Calculate peer ranking
 */
export function calculatePeerRank(
  currentScore: number,
  peerScores?: Record<string, number>
): { rank: number; total: number } {
  if (!peerScores || Object.keys(peerScores).length === 0) {
    // Estimate rank based on score alone
    const estimatedRank = currentScore >= 80 ? 1
      : currentScore >= 70 ? 2
      : currentScore >= 60 ? 3
      : currentScore >= 50 ? 4
      : 5;
    return { rank: estimatedRank, total: 6 };
  }

  const allScores = [...Object.values(peerScores), currentScore];
  const sortedScores = allScores.sort((a, b) => b - a);
  const rank = sortedScores.indexOf(currentScore) + 1;

  return { rank, total: allScores.length };
}

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate complete NATEX Score for a country
 * This is the main entry point for score calculation
 */
export function calculateNatexScore(input: CountryScoreInput): NatexScore {
  const { countryData, adjustments = [], previousScore } = input;

  // Step 1: Score all indicators
  const scoredIndicators = scoreAllIndicators(countryData);

  // Step 2: Calculate all pillars
  const pillars = calculateAllPillars(scoredIndicators);

  // Step 3: Calculate base overall score
  const baseScore = calculateOverallScore(pillars);

  // Step 4: Apply adjustments
  const adjustedScore = applyAdjustments(baseScore, adjustments);

  // Step 5: Derive signal, stars, grade
  const signal = getSignalFromScore(adjustedScore);
  const stars = getStarsFromScore(adjustedScore);
  const grade = getGradeFromScore(adjustedScore);

  // Step 6: Identify key drivers
  const keyDrivers = identifyDrivers(scoredIndicators, pillars);
  const strengths = extractStrengths(keyDrivers);
  const weaknesses = extractWeaknesses(keyDrivers);

  // Step 7: Calculate trend
  let trend: TrendDirection = TrendDirection.STABLE;
  let scoreChange = 0;

  if (previousScore !== undefined) {
    scoreChange = adjustedScore - previousScore;
    if (scoreChange > 3) trend = TrendDirection.RISING;
    else if (scoreChange < -3) trend = TrendDirection.FALLING;
  }

  // Step 8: Calculate data quality
  const dataCompleteness = calculateDataQuality(countryData);

  return {
    countryCode: countryData.countryCode,
    countryName: countryData.countryName,
    score: adjustedScore,
    baseScore,
    signal,
    stars,
    grade,
    pillars,
    adjustments,
    keyDrivers,
    strengths,
    weaknesses,
    trend,
    previousScore: previousScore ?? adjustedScore,
    scoreChange: Math.round(scoreChange * 100) / 100,
    calculatedAt: new Date(),
    dataAsOf: countryData.asOfDate,
    dataCompleteness,
    methodology: 'NATEX Opportunity Score v1.0',
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Quick score calculation (without full details)
 * Useful for comparison tables
 */
export function calculateQuickScore(input: CountryScoreInput): {
  score: number;
  signal: ScoreSignal;
  grade: string;
  stars: 1 | 2 | 3 | 4 | 5;
} {
  const scoredIndicators = scoreAllIndicators(input.countryData);
  const pillars = calculateAllPillars(scoredIndicators);
  const baseScore = calculateOverallScore(pillars);
  const adjustedScore = applyAdjustments(baseScore, input.adjustments || []);

  return {
    score: adjustedScore,
    signal: getSignalFromScore(adjustedScore),
    grade: getGradeFromScore(adjustedScore),
    stars: getStarsFromScore(adjustedScore),
  };
}

/**
 * Get score breakdown for a specific pillar
 */
export function getPillarDetail(
  countryData: CountryIndicatorData,
  pillar: Pillar
): PillarScore {
  const scoredIndicators = scoreAllIndicators(countryData);
  return calculatePillar(pillar, scoredIndicators);
}

/**
 * Simulate score with hypothetical indicator changes
 * Useful for "what-if" analysis
 */
export function simulateScore(
  input: CountryScoreInput,
  changes: Record<string, number>
): NatexScore {
  // Create modified country data
  const modifiedData: CountryIndicatorData = {
    ...input.countryData,
    indicators: { ...input.countryData.indicators },
  };

  // Apply hypothetical changes
  for (const [indicatorId, newValue] of Object.entries(changes)) {
    modifiedData.indicators[indicatorId] = newValue;
  }

  return calculateNatexScore({
    ...input,
    countryData: modifiedData,
  });
}

/**
 * Get all indicator definitions
 */
export function getIndicatorById(id: string): Indicator | undefined {
  return INDICATOR_BY_ID.get(id);
}

/**
 * Get indicators for a pillar
 */
export function getIndicatorsByPillar(pillar: Pillar): Indicator[] {
  return INDICATORS_BY_PILLAR.get(pillar) || [];
}

/**
 * Get indicators for a factor
 */
export function getIndicatorsByFactor(factor: Factor): Indicator[] {
  return INDICATORS_BY_FACTOR.get(factor) || [];
}

/**
 * Get critical indicators (those with high weight)
 */
export function getCriticalIndicators(): Indicator[] {
  return ALL_INDICATORS.filter(ind => ind.weight >= 0.35);
}
